/**
 * HotelbedsExploreCards — Phase 5 (2026-07-27).
 *
 * Featured Hotelbeds hotels rendered on /explore below the search
 * hero. Uses the SAME visual card treatment as `HotelsResultsPage`
 * (grid view) — same aspect ratio, star badge, Powered-by chip,
 * navy-typography footer — so browse/swipe feel identical whichever
 * entry surface the user landed from.
 *
 * Cache-first data source: `GET /api/hotels/featured` reads from the
 * persisted `hotelbeds_hotels_content` collection. When the cache is
 * empty (bootstrap state), the backend returns a
 * `fallback_destinations` list and we render destination "shortcut"
 * cards instead — each routes into `/hotels/search?destinationCode=…`
 * with default dates so the user still lands on live inventory.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Star, Bed, ArrowRight, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function categoryStars(catCode) {
  if (!catCode) return 0;
  const m = /^(\d)/.exec(catCode);
  return m ? Number(m[1]) : 0;
}

function defaultSearchParams(destinationCode, destinationName) {
  const occ = {
    rooms: 1,
    adults: 2,
    children: 0,
    paxes: [
      { type: "AD", age: 30 },
      { type: "AD", age: 30 },
    ],
  };
  return new URLSearchParams({
    destinationCode,
    destinationName: destinationName || "",
    checkIn: todayPlus(30),
    checkOut: todayPlus(34),
    occupancies: JSON.stringify([occ]),
  }).toString();
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl overflow-hidden bg-white border-2 border-tb-navy/10 animate-pulse">
      <div className="aspect-[4/3] bg-tb-navy/5" />
      <div className="p-4 space-y-2">
        <div className="h-5 w-3/4 bg-tb-navy/10 rounded" />
        <div className="h-3 w-1/2 bg-tb-navy/5 rounded" />
      </div>
    </div>
  );
}

// Same visual as HotelsResultsPage's `HotelCard` — kept in sync
// intentionally so browse feels identical.
function HotelCard({ hotel, onClick }) {
  const stars = categoryStars(hotel.categoryCode);
  const zone = [hotel.zoneName, hotel.destinationName].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      data-testid={`explore-hb-hotel-${hotel.code}`}
      onClick={onClick}
      className="group text-left w-full h-full flex flex-col rounded-2xl border-2 border-tb-navy/10 bg-white overflow-hidden shadow-sm hover:border-tb-navy hover:shadow-[0_18px_35px_-18px_rgba(26,58,95,0.4)] transition"
    >
      <div className="relative aspect-[4/3] bg-white overflow-hidden">
        {hotel.thumbnailUrl ? (
          <img
            src={hotel.thumbnailUrl}
            alt={hotel.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          // Neutral navy gradient placeholder — never a Google Photo.
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(15,39,64,0.9) 0%, rgba(26,58,95,0.75) 60%, rgba(230,184,74,0.35) 100%)",
            }}
          >
            <Bed className="w-14 h-14 text-white/70" strokeWidth={1.4} />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <PoweredByHotelbeds size="xs" />
        </div>
        {stars > 0 && (
          <div className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-white/90 border border-tb-navy/20">
            {Array.from({ length: stars }).map((_, i) => (
              <Star key={i} className="w-3 h-3 text-tb-gold fill-tb-gold" />
            ))}
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3
          className="text-tb-navy text-lg leading-tight line-clamp-2 font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          {hotel.name}
        </h3>
        <div className="mt-1 font-hand text-tb-navy/60 text-xs line-clamp-1">
          {zone || hotel.city || "—"}
        </div>
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            <div className="font-hand text-tb-navy/50 text-[11px] uppercase tracking-widest">
              from
            </div>
            <div
              className="text-tb-navy text-xl leading-none font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {hotel.minRate != null
                ? `${Number(hotel.minRate).toFixed(2)} ${hotel.currency || ""}`
                : "See rates"}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-tb-navy/50 group-hover:text-tb-navy group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </button>
  );
}

// Destination shortcut card — shown only when the hotel cache is
// completely empty (bootstrap). Same overall look, but the hero is
// a solid navy panel with the destination code prominent so the
// user knows this is a shortcut into `/hotels/search`.
function DestinationCard({ dest, onClick }) {
  return (
    <button
      type="button"
      data-testid={`explore-hb-destination-${dest.code}`}
      onClick={onClick}
      className="group text-left w-full h-full flex flex-col rounded-2xl border-2 border-tb-navy/10 bg-white overflow-hidden shadow-sm hover:border-tb-navy hover:shadow-md transition"
    >
      <div
        className="relative aspect-[4/3] flex items-center justify-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(15,39,64,0.95) 0%, rgba(26,58,95,0.85) 50%, rgba(230,184,74,0.6) 100%)",
        }}
      >
        <span
          className="text-white text-5xl font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          {dest.code}
        </span>
        <div className="absolute top-2 left-2">
          <PoweredByHotelbeds size="xs" />
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3
          className="text-tb-navy text-lg leading-tight font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          {dest.name}
        </h3>
        <div className="mt-1 font-hand text-tb-navy/60 text-xs">
          {dest.country}
        </div>
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div className="font-hand text-tb-navy/60 text-xs">
            Live rooms · instant confirmation
          </div>
          <ArrowRight className="w-4 h-4 text-tb-navy/50 group-hover:text-tb-navy group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </button>
  );
}

export default function HotelbedsExploreCards({ filters }) {
  const nav = useNavigate();
  const location = useLocation();
  const [hotels, setHotels] = useState([]);
  const [fallback, setFallback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/hotels/featured?limit=12")
      .then(({ data }) => {
        if (!alive) return;
        setHotels(data?.hotels || []);
        setFallback(data?.fallback_destinations || []);
        setError(null);
      })
      .catch((e) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Phase 5 (2026-07-27): apply the PhaseFourFiltersSheet output to
  // the featured grid. Featured cards don't carry live rates so
  // budget can only prune cards that we know are outside the range —
  // absent-rate cards (`minRate: null`) are ALWAYS kept so the user
  // can still click through to search availability. Property type is
  // best-effort: `hotel|resort|boutique` all map to the hotel cards,
  // `beachfront|adults_only|family` are no-ops for now (no HB signal).
  const filteredHotels = useMemo(() => {
    if (!filters) return hotels;
    const budgetMin = filters.minBudget ? Number(filters.minBudget) : 0;
    const budgetMax = filters.maxBudget ? Number(filters.maxBudget) : Infinity;
    return hotels.filter((h) => {
      const rate = Number(h.minRate);
      if (Number.isFinite(rate)) {
        if (rate < budgetMin) return false;
        if (rate > budgetMax) return false;
      }
      return true;
    });
  }, [hotels, filters]);

  const showFallback = !loading && fallback.length > 0;

  return (
    <section
      data-testid="explore-hotelbeds-cards"
      className="w-full"
    >
      <div className="flex items-end justify-between mb-4 gap-4">
        <div>
          <h2
            className="text-tb-navy text-2xl md:text-3xl leading-tight font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Featured stays
          </h2>
          <p className="mt-1 font-hand text-tb-navy/55 text-sm">
            Real availability from Stay22 — compare Booking.com, Expedia &amp; more.
          </p>
        </div>
      </div>

      {error && (
        <div
          data-testid="explore-hb-cards-error"
          className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 font-hand text-red-700 text-sm flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Couldn&apos;t load featured hotels — {formatApiErrorDetail(error)}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
        {loading && Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        {!loading && filteredHotels.map((h) => (
          <HotelCard
            key={h.code}
            hotel={h}
            onClick={() => nav(`/hotels/${h.code}`)}
          />
        ))}
        {showFallback && fallback.map((d) => (
          <DestinationCard
            key={d.code}
            dest={d}
            onClick={() => nav(`/hotels/search?${defaultSearchParams(d.code, d.name)}`, {
              state: { from: location.pathname + location.search },
            })}
          />
        ))}
      </div>
    </section>
  );
}
